/**
 * Bathroom Constraint Checker
 *
 * Uses the collision framework (shared-lib/collision) to validate
 * the current room layout against bathroom constraints.
 * Works directly with floor-plan format objects — no V2 conversion needed.
 */

import { checkPlacement } from '@shared-lib/collision';
import { getClearanceConfig } from '@shared-lib/collision/clearanceConfig';
import { collisionToRule, ruleToLabel } from './constraintFilter';
import { buildOpeningLookup } from './openingSyncUtils';

const FIXTURE_TYPES = new Set([
  'toilet',
  'sink',
  'double-sink',
  'lower-cabinet',
  'shower',
  'bathtub',
]);

function getFixtureType(obj) {
  const t = (obj.type || '').toLowerCase();
  if (FIXTURE_TYPES.has(t)) return t;
  if (obj.category) {
    for (const key of Object.keys(obj.category)) {
      const lk = key.toLowerCase();
      if (FIXTURE_TYPES.has(lk)) return lk;
    }
  }
  return null;
}

/**
 * Run all constraint checks on a room's current fixtures.
 *
 * @param {Object} roomData - Room data from Firestore
 * @param {string[]} [disabledRules] - Specific rule strings to skip
 * @param {Object} [structureData] - Parent structure with canonical
 *   `structureDoors / doors`. Needed for the door-swing check to read
 *   the canonical doorType / center; without it slim room doors yield
 *   empty values and the swing check silently passes everything.
 * @returns {Object|null} { violations: Array<{rule, label}>, disabledRules: string[] }
 */
export function checkBathroomConstraints(
  roomData,
  disabledRules = [],
  structureData
) {
  const objects = roomData.objects || [];
  const fixtures = objects.filter((obj) => {
    const type = getFixtureType(obj);
    if (!type) return false;
    const config = getClearanceConfig(obj);
    return !config.skipCollisionCheck;
  });

  if (fixtures.length === 0) return null;

  const structureDoorsById = buildOpeningLookup({
    doors: structureData?.structureDoors || structureData?.doors,
  });

  const disabledSet = new Set(disabledRules);
  const seenRules = new Set();
  const violations = [];
  const passed = [];

  for (const fixture of fixtures) {
    const fixtureType = getFixtureType(fixture);
    const result = checkPlacement(fixture, roomData, {
      excludeIds: [],
      structureDoorsById,
    });

    if (!result.valid) {
      for (const collision of result.collisions) {
        const rule = collisionToRule(collision, fixtureType);
        if (seenRules.has(rule)) continue;
        seenRules.add(rule);

        const label = ruleToLabel(rule);

        if (disabledSet.has(rule)) {
          passed.push({ rule, label, disabled: true });
        } else {
          violations.push({ rule, label });
        }
      }
    }
  }

  return { violations, passed, disabledRules };
}
